// Pure logic for the email-spy screens, kept out of the components so the
// wording and the readiness rules are unit-tested without rendering.

import moment from "moment";

// heldBack: an address minted with the watch but not shown until its
// available_date, so the two signups land days apart.
export function heldBack(address, now = Date.now()) {
  const at = address && Date.parse(address.available_date);
  return !!at && at > now;
}

// personaLine: the made-up person to sign up as with this address, so a form
// filled by hand uses the same details as the robot. "" when there is none.
export function personaLine(address) {
  if (!address) return "";
  const name = [address.first_name, address.last_name].filter(Boolean).join(" ");
  const parts = [name, address.company, address.job_title, address.country].filter(Boolean);
  return parts.length ? `Sign up as ${parts.join(" · ")}` : "";
}

// A watch needs two addresses planted days apart before anything can be told
// apart: a drip step arrives a fixed interval after each address signed up,
// while a broadcast reaches them all on one date. With one address those are
// the same observation. The screen's job is to say so and say what fixes it.
export function watchReadiness(watch) {
  const addresses = (watch && watch.address_count) || 0;
  const messages = (watch && watch.message_count) || 0;

  if (addresses < 2 && watch && watch.next_address_date) {
    return {
      ready: false,
      status: "second address coming",
      hint: `Your second address will be ready ${moment(watch.next_address_date).format("MMM D [at] h:mm a")}. Come back then and paste it into the same signup form: two signups on different dates is what separates a drip from a broadcast.`,
    };
  }
  if (addresses < 2) {
    return {
      ready: false,
      status: "needs a second address",
      hint: "Add another address and paste it into the same signup form in a few days. Two signups on different dates is what separates a drip from a broadcast.",
    };
  }
  if (messages === 0) {
    return {
      ready: false,
      status: "waiting for mail",
      hint: "Nothing has arrived yet. Check the addresses were accepted by the signup form.",
    };
  }
  return { ready: true, status: "watching", hint: "" };
}

// How far apart two plantings must be before the two kinds of timing can be
// told apart at all. Mirrors the server's tolerance.
export const MIN_PLANTING_GAP_HOURS = 12;

// True when a newly added address is too close behind the last one to be
// useful yet — worth saying before the user pastes it, not after.
export function plantedTooRecently(addresses, now = Date.now()) {
  const seen = (addresses || [])
    .map((a) => Date.parse(a.first_seen_date || a.planted_date || a.available_date || a.created_date))
    .filter((t) => !isNaN(t));
  if (seen.length < 2) return false;
  seen.sort((a, b) => b - a);
  return now - seen[0] < MIN_PLANTING_GAP_HOURS * 3600 * 1000 &&
    seen[0] - seen[1] < MIN_PLANTING_GAP_HOURS * 3600 * 1000;
}

// Minutes as a person would say them. The server rounds to what someone would
// plausibly have configured, so this never needs to show a fraction.
export function formatDelay(minutes) {
  const m = Math.max(0, Math.round(minutes || 0));
  if (m === 0) return "immediately";
  if (m < 60) return `${m} min`;
  if (m < 24 * 60) {
    const hours = Math.round(m / 60);
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }
  const days = Math.round(m / (24 * 60));
  return `${days} day${days === 1 ? "" : "s"}`;
}

// A step's position in the drip, as "day 5" rather than a raw offset.
export function formatOffset(minutes) {
  const m = Math.max(0, Math.round(minutes || 0));
  if (m < 60) return "on signup";
  if (m < 24 * 60) return `${Math.round(m / 60)}h after signup`;
  return `day ${Math.round(m / (24 * 60))}`;
}

// Unclassified captures grouped by why, so the screen shows one line per
// reason instead of a list of identical messages.
export function reasonSummary(unclassified) {
  const counts = new Map();
  (unclassified || []).forEach((c) => {
    const reason = c.reason || "not yet classified";
    counts.set(reason, (counts.get(reason) || 0) + 1);
  });
  return Array.from(counts, ([reason, count]) => ({ reason, count })).sort((a, b) => b.count - a.count);
}

// A watch can only be imported once it has produced a flow.
export function canImport(result) {
  return !!(result && Array.isArray(result.flows) && result.flows.length > 0);
}

// Flows come back keyed by group; the picker needs a label and a value.
export function flowOptions(result) {
  return ((result && result.flows) || []).map((flow) => ({
    value: flow.group,
    label: flow.label + (flow.steps ? ` — ${flow.steps.length} emails` : ""),
  }));
}

// Sorting the list of watches. A sort is { key, desc }; key is one of the
// column keys below. Status sorts from least ready to most ready.
const STATUS_ORDER = ["needs a second address", "second address coming", "waiting for mail", "watching"];

const sortValue = {
  name: (w) => ((w && w.name) || "").toLowerCase(),
  addresses: (w) => (w && w.address_count) || 0,
  collected: (w) => (w && w.message_count) || 0,
  unread: (w) => (w && w.unread_count) || 0,
  created: (w) => Date.parse(w && w.created_date) || 0,
  status: (w) => STATUS_ORDER.indexOf(watchReadiness(w).status),
};

export const WATCH_SORT_KEYS = Object.keys(sortValue);

function compare(a, b) {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

// sortWatches returns a new sorted array. Ties go by name (A to Z) whatever
// the direction. An unknown or empty sort keeps the given order.
export function sortWatches(rows, sort) {
  const list = (rows || []).slice();
  const value = sort && sortValue[sort.key];
  if (!value) return list;
  const dir = sort.desc ? -1 : 1;
  return list.sort((a, b) =>
    dir * compare(value(a), value(b)) || compare(sortValue.name(a), sortValue.name(b)));
}

// The sort as a short string ("collected" or "-collected") for saving, and back.
export function formatWatchSort(sort) {
  if (!sort || !sortValue[sort.key]) return "";
  return (sort.desc ? "-" : "") + sort.key;
}

export function parseWatchSort(text) {
  const s = typeof text === "string" ? text : "";
  const desc = s.startsWith("-");
  const key = desc ? s.slice(1) : s;
  return sortValue[key] ? { key, desc } : null;
}

// The list's sort is remembered in this browser, so coming back from a
// competitor shows the list sorted the same way.
const SORT_STORAGE_KEY = "spy.watchSort";

export function loadWatchSort() {
  try { return parseWatchSort(window.localStorage.getItem(SORT_STORAGE_KEY)); } catch (e) { return null; }
}

export function saveWatchSort(sort) {
  try {
    const text = formatWatchSort(sort);
    if (text) window.localStorage.setItem(SORT_STORAGE_KEY, text);
    else window.localStorage.removeItem(SORT_STORAGE_KEY);
  } catch (e) { /* storage blocked: the sort just isn't remembered */ }
}

// hideEmpty: drop watches that have collected no mail yet. Returns the rows
// to show and how many were hidden.
export function hideEmpty(rows, on) {
  const list = rows || [];
  if (!on) return { rows: list, hidden: 0 };
  const shown = list.filter((w) => ((w && w.message_count) || 0) > 0);
  return { rows: shown, hidden: list.length - shown.length };
}

// Whether to hide empty watches is remembered in this browser. Off by default.
const HIDE_EMPTY_STORAGE_KEY = "spy.hideEmpty";

export function loadHideEmpty() {
  try { return window.localStorage.getItem(HIDE_EMPTY_STORAGE_KEY) === "1"; } catch (e) { return false; }
}

export function saveHideEmpty(on) {
  try {
    if (on) window.localStorage.setItem(HIDE_EMPTY_STORAGE_KEY, "1");
    else window.localStorage.removeItem(HIDE_EMPTY_STORAGE_KEY);
  } catch (e) { /* storage blocked: the choice just isn't remembered */ }
}

// matchesName: the name box. Case doesn't matter and the text can be anywhere
// in the competitor's name, site or signup page.
export function matchesName(watch, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  return [watch && watch.name, watch && watch.site, watch && watch.signup_url]
    .some((text) => (text || "").toLowerCase().includes(q));
}

// The "Created" choices. value is a number of days, or "" for any time.
export const CREATED_FILTERS = [
  { value: "", label: "any time" },
  { value: "7", label: "last 7 days" },
  { value: "30", label: "last 30 days" },
  { value: "90", label: "last 90 days" },
];

function knownCreatedFilter(value) {
  return CREATED_FILTERS.some((f) => f.value === value) ? value : "";
}

// createdWithin: true when the watch was made in the last `days` days. Any
// time ("") lets everything through; a watch with no date only passes then.
export function createdWithin(watch, days, now = Date.now()) {
  const n = Number(days);
  if (!days || !n) return true;
  const at = Date.parse(watch && watch.created_date);
  if (isNaN(at)) return false;
  return now - at <= n * 24 * 3600 * 1000;
}

// filterWatches applies the name box, the created choice and "hide ones with
// no mail yet". Returns the rows to show and how many were left out.
export function filterWatches(rows, { name = "", created = "", hideEmpty: noMail = false } = {}, now = Date.now()) {
  const list = rows || [];
  const { rows: shown } = hideEmpty(list.filter((w) => matchesName(w, name) && createdWithin(w, created, now)), noMail);
  return { rows: shown, hidden: list.length - shown.length };
}

// shownNote: "12 of 40 shown" when a filter hides any, else "".
export function shownNote(shown, total) {
  return shown < total ? `${shown} of ${total} shown` : "";
}

// The name box and the created choice are remembered in this browser too.
const NAME_FILTER_STORAGE_KEY = "spy.nameFilter";
const CREATED_FILTER_STORAGE_KEY = "spy.createdFilter";

function loadText(key) {
  try { return window.localStorage.getItem(key) || ""; } catch (e) { return ""; }
}

function saveText(key, text) {
  try {
    if (text) window.localStorage.setItem(key, text);
    else window.localStorage.removeItem(key);
  } catch (e) { /* storage blocked: the choice just isn't remembered */ }
}

export function loadNameFilter() { return loadText(NAME_FILTER_STORAGE_KEY); }
export function saveNameFilter(text) { saveText(NAME_FILTER_STORAGE_KEY, text || ""); }
export function loadCreatedFilter() { return knownCreatedFilter(loadText(CREATED_FILTER_STORAGE_KEY)); }
export function saveCreatedFilter(value) { saveText(CREATED_FILTER_STORAGE_KEY, knownCreatedFilter(value)); }

// -- Choosing competitors to move to another org --

// keepShown: the chosen ids that are still in the list as shown, so a filter
// never leaves hidden rows chosen.
export function keepShown(selected, shownRows) {
  const shown = new Set((shownRows || []).map((w) => w.id));
  return (selected || []).filter((id) => shown.has(id));
}

// toggleOne adds or removes one id.
export function toggleOne(selected, id, on) {
  const rest = (selected || []).filter((x) => x !== id);
  return on ? rest.concat(id) : rest;
}

// toggleAllShown chooses every shown row, or clears them.
export function toggleAllShown(selected, shownRows, on) {
  const ids = (shownRows || []).map((w) => w.id);
  const rest = (selected || []).filter((id) => !ids.includes(id));
  return on ? rest.concat(ids) : rest;
}

// allShownState: how the header box looks. Ticked when every shown row is
// chosen, half-ticked when some are.
export function allShownState(selected, shownRows) {
  const chosen = keepShown(selected, shownRows).length;
  const total = (shownRows || []).length;
  return { checked: total > 0 && chosen === total, indeterminate: chosen > 0 && chosen < total };
}

// otherOrgs: the user's orgs other than the one they are in now.
export function otherOrgs(orgs, activeOrgId) {
  return (orgs || []).filter((o) => o && o.id && o.id !== activeOrgId);
}

function competitors(n) {
  return n === 1 ? "1 competitor" : `${n} competitors`;
}

export function moveConfirmText(n, orgName) {
  return `Move ${competitors(n)} and all their mail to ${orgName || "that organization"}?`;
}

export function movedText(n) {
  return n ? `Moved ${competitors(n)}` : "Nothing was moved";
}

// moveNoteLines turns the server's notes into "Acme: the other organization
// already watches this site", using the names from the list before it reloads.
export function moveNoteLines(notes, rows) {
  const names = new Map((rows || []).map((w) => [w.id, w.name]));
  return (notes || []).map((n) => `${names.get(n.watch_id) || "A competitor"}: ${n.note}`);
}
